import {Injectable} from '@angular/core';
import { SessionService } from '@edl-development/edl-common';
import { HOURS_MAX_DIGITS, HOURS_MAX_DIGITS_AFTER_DECIMAL } from 'src/app/constants/a48.constants';
import { CplA48UserSubmissionDetail } from 'src/app/model/a48/cpl-a48-user-submission-detail.model';
import { CplA48Labels } from 'src/app/model/cpl-a48-labels.model';
import { CplA48 } from 'src/app/model/cpl-a48.model';
import { A48ActivityType } from 'src/app/model/enum/a48-activity-type.enum';
import { A48CourseTypes } from 'src/app/model/enum/a48-sub-type.enum';
import { A48UserStatus } from 'src/app/model/enum/a48-user-status.enum';
import { PortalHelpersService } from '../portal-helpers/portal-helpers.service';
import { A48SubmissionContextCode } from 'src/app/model/enum/a48-submission-context-code.enum';

@Injectable()
export class A48HelpersService {
    constructor(private sessionService: SessionService, private portalHelpersService: PortalHelpersService) { }

    canSave({ StatusID, ParaStatusID, isReadOnly, isItemComplete } : { StatusID: number, ParaStatusID: number, isReadOnly: boolean, isItemComplete: boolean }) {
        return !isReadOnly &&
            (StatusID == A48UserStatus.ERROR || 
                    (!isItemComplete && ParaStatusID != A48UserStatus.PARAAPPROVED &&
                        (StatusID == A48UserStatus.NOACTION || 
                            StatusID == A48UserStatus.OVERRIDE ||
                            StatusID == A48UserStatus.REMOVED
                        )
                    ));
    }

    canResubmit({ StatusID, ParaStatusID, isReadOnly, IsAdminView, isItemComplete, ShouldSubmitToPerms } :
        {StatusID: number, ParaStatusID: number, isReadOnly: boolean, IsAdminView: boolean, isItemComplete: boolean, ShouldSubmitToPerms: boolean}) {
        return this.canSave({ StatusID, ParaStatusID, isReadOnly, isItemComplete }) && IsAdminView && isItemComplete && ShouldSubmitToPerms;
    }

    isCredits(ActivityType: A48ActivityType) {
        return ActivityType === A48ActivityType.CREDITS;
    }

    getHoursOrCreditsLabel(hours: number, ActivityType: A48ActivityType) {
        const isCredits = this.isCredits(ActivityType);
        return `${hours} ${isCredits ? 'C' : 'H'}`
    }

    getHoursOrCreditsFullLabel(ActivityType: A48ActivityType) {
        const isCredits = this.isCredits(ActivityType);
        return isCredits ? 'Credits' : 'Hours';
    }

    getHoursError(ActivityType: A48ActivityType, errors: any) {
        const label = this.getHoursOrCreditsFullLabel(ActivityType);
        if (errors) {
            if (errors.required) return 'This field is required.';
            if (errors.isGreaterThanZero) return `${label} must be greater than zero.`;
            if (errors.improperFormat) return 'Improper number format.';
            if (errors.tooManyDigits) return `${label} may only have up to ${HOURS_MAX_DIGITS} digits.`;
            if (errors.tooManyDigitsAfterDecimal) return `${label} may only have up to ${HOURS_MAX_DIGITS_AFTER_DECIMAL} digits after the decimal.`;
        }

        return '';
    }

    getEventNameLabel(ActivityType: A48ActivityType) {
        const isCredits = this.isCredits(ActivityType);
        return isCredits ? 'Course Name and Course Number (if Applicable)' : 'Event Name';
    }

    getAct48DetailsLabels(a48: CplA48 | CplA48UserSubmissionDetail) {
        const portalSettings = this.sessionService.PortalSettings;
        const hasMultipleAUN = portalSettings.AUNSettings?.length > 1;
        const AUN = (a48?.AUN && hasMultipleAUN) ? a48?.AUN : null;
        const AUNName = (a48?.AUN && hasMultipleAUN) ? 
            (this.portalHelpersService.getAUNSettings(portalSettings, a48?.AUN)?.AUNName 
                ?? this.portalHelpersService.createAUNSettings(a48?.AUN)?.AUNName) :
            null;
        const AUNLabel = (AUN && AUNName) ? `${AUNName} (${AUN})` : '';

        const a48CourseTypes = new A48CourseTypes();
        const cecPerformanceId = (<CplA48> a48).CecPerformance ?? (<CplA48UserSubmissionDetail> a48).CECPerformanceStandard;
        const ActivityType = a48CourseTypes.activityTypes.filter(x => x.id == a48.ActivityType)[0]?.name ?? '';
        const CollegeCreditType = this.isCredits(a48.ActivityType) ? (a48CourseTypes.collegeCreditType.filter(x => x.id == a48.CollegeCreditType)[0]?.name ?? '') : '';
        const CourseType = a48CourseTypes.courseType.filter(x => x.id == a48.CourseType)[0]?.name ?? '';
        const CECPerformanceStandard = a48CourseTypes.cecPerformance.filter(x => x.id == cecPerformanceId)[0]?.name ?? '';


        return new CplA48Labels({
            AUNLabel,
            ActivityType,
            CollegeCreditType,
            CourseType,
            CECPerformanceStandard
        });
    }

    canDisplaySubmitHoursToggle({
        hasAct48,
        isUserPara,
        hasPPID,
        excludeFromSubmission,
        contextCode
    } : {
        hasAct48: boolean
        isUserPara: boolean
        hasPPID: boolean,
        excludeFromSubmission: boolean,
        contextCode: A48SubmissionContextCode
    }) {
        return hasAct48 
            && !excludeFromSubmission 
            && contextCode !== A48SubmissionContextCode.EMERGENCY_CERT_SUBMISSION 
            && (!isUserPara || hasPPID);
    }
}